import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { FileText, ExternalLink, Loader2 } from 'lucide-react';
import { labApi } from '../services/api';
import ScrollReveal from './ui/ScrollReveal';
import Skeleton from './ui/Skeleton';

const Publications = () => {
  const [publications, setPublications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [visibleCount, setVisibleCount] = useState(4);
  const [sortOrder, setSortOrder] = useState('newest');

  const sortedPublications = [...publications].sort((a, b) => {
    const yearA = parseInt(a.year) || 0;
    const yearB = parseInt(b.year) || 0;
    return sortOrder === 'newest' ? yearB - yearA : yearA - yearB;
  });

  useEffect(() => {
    labApi.getPublications()
      .then(data => setPublications(data))
      .catch(err => console.error('Failed to fetch publications:', err))
      .finally(() => setLoading(false));
  }, []);

  return (
    <section id="publications" className="py-28 bg-gray-50" data-testid="publications-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <ScrollReveal>
          <div className="flex flex-col md:flex-row justify-between items-center mb-16 gap-6">
            <div className="text-left md:text-left">
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gradient-section mb-4 tracking-[-0.02em]" data-testid="publications-heading">
                Publications
              </h2>
              <div className="heading-accent-bar mx-0 mb-4"></div>
              <p className="text-lg text-slate-500 max-w-2xl font-light tracking-wide">
                Our research contributions to advancing knowledge in materials science,
                nanotechnology, and smart agriculture systems.
              </p>
            </div>
            
            <div className="flex items-center gap-2 self-start md:self-end bg-white border border-gray-200 px-4 py-2 rounded-full shadow-sm">
              <span className="text-sm text-slate-500 font-medium">Sort by:</span>
              <select 
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                className="bg-transparent text-sm font-semibold text-slate-800 outline-none cursor-pointer focus:ring-0"
              >
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
              </select>
            </div>
          </div>
        </ScrollReveal>

        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-32 w-full rounded-xl" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {sortedPublications.slice(0, visibleCount).map((pub, index) => (
              <ScrollReveal key={pub.id} delay={(index % 2) * 150}>
                <Card 
                  data-testid={`publication-card-${pub.id}`}
                  className="hover-card-glow border-gray-200 bg-white"
                >
                  <CardHeader>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <CardTitle className="text-lg font-semibold text-slate-800 mb-2 leading-tight">
                          {pub.title}
                        </CardTitle>
                        <p className="text-sm text-slate-600 mb-1">{pub.authors}</p>
                        <p className="text-sm text-slate-500">
                          {pub.journal} • {pub.year}
                        </p>
                      </div>
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 rounded-full bg-teal-50 flex items-center justify-center">
                          <FileText className="text-teal-600" size={24} />
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        data-testid={`publication-doi-${pub.id}`}
                        className="text-teal-600 border-teal-200 hover:bg-teal-50 hover:text-teal-700 transition-colors duration-200"
                        onClick={() => window.open(`https://doi.org/${pub.doi}`, '_blank')}
                      >
                        <ExternalLink size={14} className="mr-1" />
                        DOI: {pub.doi.split('/').pop()}
                      </Button>
                      {pub.pdf !== '#' && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="text-slate-600 border-gray-200 hover:bg-gray-50 transition-colors duration-200"
                          onClick={() => window.open(pub.pdf, '_blank')}
                        >
                          <FileText size={14} className="mr-1" />
                          PDF
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </ScrollReveal>
            ))}
          </div>
        )}
        
        <div className="flex flex-col items-center gap-4 mt-12">
          {visibleCount < publications.length && (
            <button 
              onClick={() => setVisibleCount(prev => prev + 4)}
              className="px-8 py-3 bg-white border-2 border-teal-600 text-teal-600 font-semibold rounded-full hover:bg-teal-50 transition-colors duration-300 shadow-sm"
            >
              Load More Publications
            </button>
          )}
          {visibleCount > 4 && (
            <button 
              onClick={() => {
                setVisibleCount(4);
                document.getElementById('publications')?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="text-sm font-medium text-slate-400 hover:text-slate-600 transition-colors duration-300 flex items-center gap-1"
            >
              Show Less ⬆
            </button>
          )}
        </div>
      </div>
    </section>
  );
};

export default Publications;
